<script>
import { mapActions } from "vuex"
import { match } from "path-to-regexp"

export default {
    name: "SidebarMenuItem",
    props: {
        item: { type: Object, required: true },
        collapse: { type: Boolean, required: true },
    },
    data() {
        return {
            isCollapsed: true,
            isLoading: false,
            loadedChildren: [],
            abortController: null,
        }
    },
    watch: {
        collapse: {
            handler: function (newVal) {
                if (newVal === true) {
                    this.isCollapsed = newVal
                }
            },
            immediate: true,
        },
        item: {
            handler: function (item) {
                if (item.filtered && item.type !== "device") {
                    this.isCollapsed = false
                }
            },
            immediate: true,
        },
        $route: {
            handler: function (route) {
                if (this.abortController) {
                    this.abortController.abort()
                }

                this.abortController = new AbortController()

                if (route.path === this.item.href) {
                    this.setActiveMenuItem(this.item)

                    if (this.item.type === "location") {
                        this.getLocationMenu()
                    }
                }

                if (!this.collapse) {
                    this.isCollapsed = this.isRouteCollapsed(this.item)
                }
            },
            immediate: true,
        },
    },
    computed: {
        children: function () {
            return [].concat(this.item?.children, this.loadedChildren)
        },
        isDevice: function () {
            return this.item.type === "device"
        },
        iconClass: function () {
            switch (this.item.type) {
                case "company":
                    return "fa-landmark"
                case "location-group":
                    return "fa-sitemap"
                case "location":
                    return "fa-house"
                case "device-group":
                    return "fa-object-ungroup"
                default:
                    return null
            }
        },
        deviceCategoryIcon: function () {
            if (!this.isDevice) {
                return null
            }

            switch (this.item.data.category.value) {
                case "OTHER":
                    return "icon-washing-machine"
                case "EV_CHARGER":
                    return "icon-car"
                case "SOLAR_POWER_PLANT":
                    return "icon-sun"
                case "BATTERY":
                    return "icon-battery"
                case "HYBRID_INVERTER":
                    return "icon-hybrid"
                case "HVAC":
                    return "icon-heat-pump"
                case "GRID":
                    return "icon-system"
                default:
                    return "icon-other"
            }
        },
        hasChildren: function () {
            return this.children.length > 0
        },
        itemLeftPadding: function () {
            let paddingStep = 10

            return `${paddingStep * this.item.level}px`
        },
        isActive() {
            return !this.isCollapsed && this.item.level <= 1
        },
        isSubItemActive() {
            return this.item.level > 1 && !this.isCollapsed
        },
    },
    methods: {
        ...mapActions("sidebar", {
            setActiveMenuItem: "setActiveMenuItem",
        }),
        openItem() {
            this.isCollapsed = !this.isCollapsed
        },
        isChildCollapsed: function (child) {
            return this.isCollapsed || this.isRouteCollapsed(child)
        },
        isRouteCollapsed(item) {
            if (item.filtered) {
                return false
            }

            const aliases = item.aliases
            const childrenOpen = aliases.some((alias) => {
                // Convert the alias to a RegExp pattern using `match`
                const matchFn = match(alias, { decode: decodeURIComponent })
                const result = matchFn(this.$route.path)

                // The route is active if the result is not false
                return result !== false
            })

            return !childrenOpen
        },
        getLocationMenu() {
            this.isLoading = true
            const locationId = this.item.data.location.id

            this.axios
                .get(`/menu/locations/${locationId}`, {
                    signal: this.abortController.signal,
                    params: {
                        level: this.item.level,
                        prefix: this.item.href,
                    },
                })
                .then((response) => {
                    this.loadedChildren = response.data.data.structure
                    this.isCollapsed = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        onItemClick() {
            let route = this.item.href

            if (this.item.children?.length) {
                this.isCollapsed = !this.isCollapsed
            }

            if (this.$route.path === route) {
                return
            }

            if (this.isCollapsed) {
                this.isCollapsed = false
            }

            if (route.length === 0) {
                return
            }

            if (this.item.type === "location") {
                this.getLocationMenu()
            }

            this.$router.push(route)

            this.saveScrollPosition()
        },
        saveScrollPosition() {
            const sidebarWrapperEl = document.getElementById("sidebar-content-wrapper")

            if (sidebarWrapperEl) {
                const scrollTop = sidebarWrapperEl.scrollTop
                const currentRoute = this.$route.path
                const scrollData = {
                    route: currentRoute,
                    position: scrollTop,
                }

                localStorage.setItem("sidebarScrollPosition", JSON.stringify(scrollData))
            }
        },
        restoreScrollPosition() {
            const scrollPosition = localStorage.getItem("sidebarScrollPosition")

            if (scrollPosition) {
                const scrollData = JSON.parse(scrollPosition)
                const currentRoute = this.$route.path
                const sidebarWrapperEl = document.getElementById("sidebar-content-wrapper")

                if (scrollData && scrollData.route === currentRoute && sidebarWrapperEl) {
                    sidebarWrapperEl.scrollTop = scrollData.position
                }
            }
        },
    },
    mounted() {
        this.restoreScrollPosition()
    },
}
</script>

<template>
    <div
        class="sidebar-menu-item"
        :class="[
            `menu-item-level-${item.level}`,
            {
                'menu-item-active': isActive,
                'sub-menu-item-active': isSubItemActive,
                'filtered-title': item?.filteredTitle,
            },
        ]"
        @click.stop="onItemClick"
    >
        <div class="menu-item-title" :style="{ paddingLeft: itemLeftPadding }">
            <span
                v-if="item?.filteredTitle"
                class="fa fa-solid fa-circle fa-2xs filtered-indicator"
            ></span>
            <div class="d-flex justify-content-between align-items-center">
                <span v-if="isDevice" :class="['fa', `icon`, deviceCategoryIcon, `mr-2`]"></span>
                <span v-else :class="['fa', 'fa-solid', 'fa-lg', iconClass, 'mr-2']"></span>
                <span>{{ item.title }}</span>
            </div>
            <template v-if="hasChildren">
                <span @click.stop="openItem" class="expand-icon-wrapper">
                    <i v-if="isCollapsed" class="fa fa-plus"></i>
                    <i v-else class="fa fa-minus"></i>
                </span>
            </template>
        </div>

        <div
            v-if="hasChildren && !isCollapsed"
            class="menu-item-children"
            :class="{ collapsed: isCollapsed }"
            id="children"
        >
            <SidebarMenuItem
                :item="childItem"
                :key="childItem.name"
                v-for="childItem in children"
                :collapse="isChildCollapsed(childItem)"
            />
        </div>
    </div>
</template>

<style>
.expand-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin: -5px;
    cursor: pointer;
}
</style>
